import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowsRightLeftIcon, CheckCircleIcon, DocumentTextIcon, EyeIcon, PaperAirplaneIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, ConfirmModal, EmptyState, FileUpload, Modal, RadioGroup, RowMenu, SectionTitle, Table, Tag, Textarea } from "../../ui";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY } from "../model";
import ApplicationDialog from "../review/ApplicationDialog";
import { formatDateTime, formatScore, fullName, openDocument } from "../review/model";
import { SPONSOR_ANSWERS, genderOf, nomineesKey, sponsorAnswerOf, useNominees } from "./model";

const MAX_REASON = 1000;
const ANSWER_OPTIONS = [
  { value: "true", label: "Grant the scholarship" },
  { value: "false", label: "Not granted" },
];

const answerTag = (n) => <Tag color={sponsorAnswerOf(n.state).color}>{sponsorAnswerOf(n.state).label}</Tag>;

/**
 * The sponsor's answer to one or more nominees, the same answer and reason for each. The owner
 * answers for their sponsor; the HEC Admin (`forSponsor`) records it for one nominee and must attach
 * the sponsor's letter. The answers are sent one by one; `onDone(fresh, failedIds)` gets the latest
 * nominee list and the nominees whose answer was not recorded.
 */
function AnswerDialog({ nominees, initial, forSponsor, onClose, onDone }) {
  const [granted, setGranted] = useState(initial);
  const [reason, setReason] = useState("");
  const [letter, setLetter] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const missing = {
      ...(!granted && { granted: "Choose the partner's answer." }),
      ...(granted === "false" && !reason.trim() && { reason: "Say why the scholarship is not granted. The student sees this reason." }),
      ...(forSponsor && !letter && { letter: "Attach the partner's letter." }),
    };
    setErrors(missing);
    if (Object.keys(missing).length) return;
    setBusy(true);
    let body = { granted: granted === "true", ...(reason.trim() && { reason: reason.trim() }) };
    if (forSponsor) {
      body = new FormData();
      body.append("granted", granted);
      if (reason.trim()) body.append("reason", reason.trim());
      body.append("letter", letter);
    }
    // One after another: each answer may change the scholarship the next one is checked against
    let fresh = null;
    const failed = [];
    for (const nominee of nominees) {
      try {
        ({ data: fresh } = await scholarshipsApi.post(`staff/applications/${nominee.applicationId}/sponsor-decision`, body));
      } catch (err) {
        console.error(`Failed to record the partner's answer for application ${nominee.applicationId}:`, err);
        failed.push({ nominee, err });
      }
    }
    setBusy(false);

    const verb = granted === "true" ? "granted" : "rejected";
    const answered = nominees.length - failed.length;
    if (answered) toast.success(nominees.length === 1 ? `${fullName(nominees[0].applicant)}: ${verb}` : `${answered} nominee${answered === 1 ? "" : "s"} ${verb}`);
    if (failed.length) {
      const message = errorMessageOf(failed[0].err, "Failed to record the answer. Please try again.");
      setErrors(fieldErrorsOf(problemOf(failed[0].err)));
      toast.error(nominees.length === 1 ? message : `Not recorded for ${failed.map((f) => fullName(f.nominee.applicant)).join(", ")}: ${message}`);
    }
    if (fresh) onDone(fresh, failed.map((f) => f.nominee.applicationId));
  };

  const single = nominees.length === 1;
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissible={!busy}
      title={single ? `Answer for ${fullName(nominees[0].applicant)}` : `Answer for ${nominees.length} nominees`}
      description={
        forSponsor
          ? "Record the partner's answer as written in its letter."
          : `Only the partner grants this scholarship. ${single ? "The student sees your answer." : "Each student sees your answer, and the same reason if you reject."}`
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} loading={busy}>
            Record the answer
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!single && <p className="text-sm text-slate-600">{nominees.map((n) => fullName(n.applicant)).join(", ")}</p>}
        <RadioGroup label="The partner's answer" name="granted" required options={ANSWER_OPTIONS} value={granted} onChange={(e) => setGranted(e.target.value)} error={errors.granted} disabled={busy} />
        <Textarea
          label={granted === "false" ? "Reason" : "Reason (only when not granted)"}
          required={granted === "false"}
          rows={3}
          maxLength={MAX_REASON}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={errors.reason}
          disabled={busy || granted !== "false"}
        />
        {forSponsor && (
          <FileUpload label="The partner's letter" required accept="application/pdf,image/jpeg,image/png" hint="PDF, JPEG or PNG, up to 10 MB" file={letter} onChange={setLetter} error={errors.letter} />
        )}
      </div>
    </Modal>
  );
}

/**
 * A sponsored scholarship's nominees once the list is sent, with the sponsor's answers. The owner
 * (`isOwner`) answers one nominee, or the selected ones at once. HEC sees the same list, records an answer with the sponsor's
 * letter, sends a reserve in a refused nominee's place, and closes the nominations.
 * `onRecordProgress(applicationId)` opens a granted nominee's follow-up with the awardees.
 */
export default function NomineesPanel({ scholarship, isOwner, basePath, onRecordProgress }) {
  const queryClient = useQueryClient();
  const { data, isPending, isError, refetch } = useNominees(scholarship.id);
  const [answering, setAnswering] = useState(null); // { nominees, initial }
  const [selected, setSelected] = useState([]); // application IDs the owner answers at once
  const [sending, setSending] = useState(null); // a reserve to send to the sponsor
  const [closing, setClosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);

  if (isError) return <EmptyState title="Could not load the nominees" onRefresh={refetch} />;

  const nominees = data?.nominees ?? [];
  const reserves = data?.reserves ?? [];
  const waitingNominees = nominees.filter((n) => n.state === "WITH_SPONSOR");
  const waiting = waitingNominees.length;
  const refused = nominees.filter((n) => n.state === "REJECTED").length;
  const open = scholarship.state !== "DECIDED";
  const counts = nominees.reduce((acc, n) => ({ ...acc, [n.state]: (acc[n.state] ?? 0) + 1 }), {});

  // Only nominees still waiting for the answer can be selected
  const chosen = waitingNominees.filter((n) => selected.includes(n.applicationId));
  const allSelected = waiting > 0 && chosen.length === waiting;
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Nominees whose answer failed stay selected, to try again
  const done = (fresh, failedIds = []) => {
    queryClient.setQueryData(nomineesKey(scholarship.id), fresh);
    // The scholarship may now be decided, and granted awardees join the follow-up
    queryClient.invalidateQueries({ queryKey: SCHOLARSHIPS_KEY });
    setSelected(failedIds);
    setAnswering(null);
    setSending(null);
    setClosing(false);
  };

  const adminCall = async (work, success, what) => {
    setBusy(true);
    try {
      const { data: fresh } = await work();
      toast.success(success);
      done(fresh);
    } catch (err) {
      console.error(`Failed to ${what} on scholarship ${scholarship.id}:`, err);
      toast.error(errorMessageOf(err, "Something went wrong. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const sendReserve = (reserve) =>
    adminCall(
      () => scholarshipsApi.post(`admin/scholarships/${scholarship.id}/nominees`, { applicationId: reserve.applicationId }),
      `${fullName(reserve.applicant)} sent to the partner`,
      `send reserve ${reserve.applicationId}`
    );
  const closeNominations = () =>
    adminCall(() => scholarshipsApi.post(`admin/scholarships/${scholarship.id}/close-nominations`), "Nominations closed", "close the nominations");

  const person = [
    { key: "rank", label: "HEC rank", accessor: (n) => n.rank ?? "—", sortValue: (n) => n.rank ?? Infinity },
    { key: "applicant", label: "Nominee", accessor: (n) => fullName(n.applicant), render: (n) => <span className="font-medium text-slate-800">{fullName(n.applicant)}</span> },
    { key: "gender", label: "Gender", accessor: (n) => genderOf(n.applicant.gender), filter: "Any gender" },
    { key: "email", label: "Email", accessor: (n) => n.applicant.email },
    { key: "phone", label: "Phone", accessor: (n) => n.applicant.phoneNumber ?? "—" },
    { key: "meanScore", label: "HEC score", accessor: (n) => formatScore(n.meanScore), sortValue: (n) => n.meanScore },
    { key: "programme", label: "Programme of study", accessor: (n) => n.programme.name, filter: "Any programme" },
    { key: "employer", label: "Employer", accessor: (n) => n.employer ?? "—" },
  ];

  const nomineeColumns = [
    ...(isOwner && waiting > 0
      ? [
          {
            key: "select",
            label: (
              <input
                type="checkbox"
                aria-label="Select every nominee waiting for your answer"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? [] : waitingNominees.map((n) => n.applicationId))}
                className="h-4 w-4 accent-brand"
              />
            ),
            exportable: false,
            sortable: false,
            render: (n) =>
              n.state === "WITH_SPONSOR" && (
                <input
                  type="checkbox"
                  aria-label={`Select ${fullName(n.applicant)}`}
                  checked={selected.includes(n.applicationId)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(n.applicationId)}
                  className="h-4 w-4 accent-brand"
                />
              ),
          },
        ]
      : []),
    ...person,
    {
      key: "answer",
      label: "Partner's answer",
      accessor: (n) => sponsorAnswerOf(n.state).label,
      filter: "Any answer",
      render: (n) => (
        <div className="flex flex-col gap-1">
          <span className="flex flex-wrap items-center gap-1">
            {answerTag(n)}
            {n.replacesRefusal && <Tag color="brand">From the reserves</Tag>}
          </span>
          {n.reason && <span className="max-w-xs text-xs text-slate-500">{n.reason}</span>}
          {n.answeredBy === "SCHOLARSHIP_ADMIN" && <span className="text-xs text-slate-500">Recorded by HEC from the partner's letter</span>}
        </div>
      ),
    },
    { key: "answeredAt", label: "Answered", accessor: (n) => formatDateTime(n.answeredAt), sortValue: (n) => n.answeredAt },
    {
      key: "actions",
      label: "Actions",
      exportable: false,
      render: (n) => {
        const waiting = n.state === "WITH_SPONSOR";
        return (
          <RowMenu
            actions={[
              { Icon: EyeIcon, label: "View", onClick: () => setOpenId(n.applicationId) },
              n.hasLetter && { Icon: DocumentTextIcon, label: "Letter", onClick: () => openDocument(`staff/applications/${n.applicationId}/sponsor-letter/download-url`) },
              waiting && isOwner && { Icon: CheckCircleIcon, label: "Grant", onClick: () => setAnswering({ nominees: [n], initial: "true" }) },
              waiting && isOwner && { Icon: XCircleIcon, label: "Not granted", onClick: () => setAnswering({ nominees: [n], initial: "false" }) },
              waiting && !isOwner && { Icon: DocumentTextIcon, label: "Record answer", onClick: () => setAnswering({ nominees: [n], initial: "" }) },
              // Granted: their progress is recorded with the awardees
              n.state === "GRANTED" && onRecordProgress && { Icon: ArrowsRightLeftIcon, label: "Record admission", onClick: () => onRecordProgress(n.applicationId) },
            ]}
          />
        );
      },
    },
  ];

  const reserveColumns = [
    ...person,
    {
      key: "actions",
      label: "Actions",
      exportable: false,
      render: (r) => (
        <RowMenu
          actions={[
            { Icon: EyeIcon, label: "View", onClick: () => setOpenId(r.applicationId) },
            open && { Icon: PaperAirplaneIcon, label: "Send to the partner", onClick: () => setSending(r) },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2">
        {SPONSOR_ANSWERS.map((a) => (
          <Tag key={a.value} color={a.color}>
            {a.label}: {counts[a.value] ?? 0}
          </Tag>
        ))}
      </div>
      <Table
        columns={nomineeColumns}
        data={nominees}
        onRowClick={(n) => setOpenId(n.applicationId)}
        isLoading={isPending}
        exportName={`nominees-${scholarship.name}`}
        emptyTitle="No nominees yet. They appear here once the DG signs off HEC's nominations."
        actions={
          isOwner &&
          waiting > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" icon={<XCircleIcon className="h-4 w-4" />} onClick={() => setAnswering({ nominees: chosen, initial: "false" })} disabled={!chosen.length}>
                Not granted{chosen.length ? ` (${chosen.length})` : ""}
              </Button>
              <Button icon={<CheckCircleIcon className="h-4 w-4" />} onClick={() => setAnswering({ nominees: chosen, initial: "true" })} disabled={!chosen.length}>
                Grant{chosen.length ? ` (${chosen.length})` : ""}
              </Button>
            </div>
          )
        }
      />
      {!isOwner && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <SectionTitle
              className="w-auto flex-1"
              title="Reserves"
              subtitle={refused ? "Send the next reserve to the partner in place of a nominee who was not granted." : "Reserves can be sent to the partner when a nominee is not granted."}
            />
            {open && (
              <Button variant="outline" onClick={() => setClosing(true)} disabled={busy || waiting > 0 || isPending}>
                Close nominations
              </Button>
            )}
          </div>
          {open && waiting > 0 && <p className="text-sm text-slate-500">The nominations can be closed once the partner has answered every nominee.</p>}
          <Table columns={reserveColumns} data={reserves} isLoading={isPending} searchable={false} exportName={`reserves-${scholarship.name}`} emptyTitle="No reserves left" />
        </div>
      )}
      {answering && (
        <AnswerDialog nominees={answering.nominees} initial={answering.initial} forSponsor={!isOwner} onClose={() => setAnswering(null)} onDone={done} />
      )}
      <ConfirmModal
        open={Boolean(sending)}
        onClose={() => setSending(null)}
        onConfirm={() => sendReserve(sending)}
        loading={busy}
        title="Send this reserve to the partner?"
        description={sending ? `${fullName(sending.applicant)} will wait for the partner's answer, and sees "With the partner".` : ""}
        confirmLabel="Send"
      />
      <ConfirmModal
        open={closing}
        onClose={() => setClosing(false)}
        onConfirm={closeNominations}
        loading={busy}
        destructive
        title="Close the nominations?"
        description={`No more reserves can be sent. ${reserves.length ? `The ${reserves.length} reserve${reserves.length === 1 ? "" : "s"} left will be not selected. ` : ""}The scholarship becomes decided.`}
        confirmLabel="Close nominations"
      />
      <ApplicationDialog key={openId} applicationId={openId} programmes={scholarship.programmes} basePath={basePath} onClose={() => setOpenId(null)} />
    </div>
  );
}
